import logger from '../utils/logger';
import DeltaSyncManager from './DeltaSyncManager';
import api from './api';

/**
 * Unified Cache Manager with Delta Sync support
 * Based on AWS AppSync Delta Sync patterns
 * 
 * Architecture:
 * - Base Query: Initial full data fetch (runs on first load + periodic refresh)
 * - Delta Query: Incremental updates (runs on navigation/reconnect)
 * - Cache: In-memory + localStorage for persistence
 * - Events: Notify subscribers when cache updates
 */
class CacheManager {
  constructor() {
    // In-memory cache storage
    this.cache = new Map();
    
    // Cache subscribers for React re-rendering
    this.subscribers = new Map();
    
    // Track last fetch times to prevent rapid refetches
    this.lastFetchTimes = {};
    
    // Configuration per content type (Production settings with Delta Sync)
    this.config = {
      products: { maxAge: 5 * 60 * 1000, refreshThreshold: 30 * 1000 }, // 5min cache, 30s refetch threshold
      categories: { maxAge: 60 * 60 * 1000, refreshThreshold: 5 * 60 * 1000 }, // 1hr cache, 5min threshold
      banners: { maxAge: 10 * 60 * 1000, refreshThreshold: 60 * 1000 }, // 10min cache, 1min threshold
      default: { maxAge: 5 * 60 * 1000, refreshThreshold: 30 * 1000 }
    };
    
    // localStorage prefix
    this.storagePrefix = 'mmart_cache_';
    
    logger.info('[CacheManager] Initialized');
  }

  /**
   * Get data with automatic delta sync
   * Returns cached data immediately, updates in background if stale
   * 
   * @param {string} url - API endpoint
   * @returns {Promise<any>} Cached or fresh data
   */
  async get(url) {
    const normalizedUrl = this.normalizeUrl(url);
    const contentType = this.getContentType(normalizedUrl);
    const config = this.config[contentType] || this.config.default;
    
    // Check in-memory cache first
    if (this.cache.has(normalizedUrl)) {
      const cached = this.cache.get(normalizedUrl);
      const age = Date.now() - cached.timestamp;
      
      // If fresh, return immediately
      if (age < config.maxAge) {
        logger.debug(`[Cache] Fresh hit: ${normalizedUrl} (age: ${age}ms)`);
        return cached.data;
      }
      
      // Stale cache - return it but update in background
      logger.debug(`[Cache] Stale hit: ${normalizedUrl} (age: ${age}ms)`);
      this.updateInBackground(normalizedUrl);
      return cached.data;
    }
    
    // Check localStorage
    const stored = this.loadFromStorage(normalizedUrl);
    if (stored) {
      logger.debug(`[Cache] localStorage hit: ${normalizedUrl}`);
      
      // Load into memory cache
      this.cache.set(normalizedUrl, {
        data: stored.data,
        timestamp: stored.timestamp
      });
      
      // Update in background if stale
      if (stored.isStale) {
        this.updateInBackground(normalizedUrl);
      }
      
      return stored.data;
    }
    
    // No cache - fetch fresh data (blocking)
    logger.debug(`[Cache] Miss: ${normalizedUrl} - fetching fresh data`);
    return await this.fetchFresh(normalizedUrl);
  }

  /**
   * Update cache in background with delta sync
   * 
   * @param {string} url - API endpoint
   */
  async updateInBackground(url) {
    const normalizedUrl = this.normalizeUrl(url);
    const lastFetch = this.lastFetchTimes[normalizedUrl] || 0;
    const timeSinceFetch = Date.now() - lastFetch;
    const config = this.config[this.getContentType(normalizedUrl)] || this.config.default;
    
    logger.info(`[Cache] 🔄 Background update triggered for ${normalizedUrl}`);
    logger.info(`[Cache] ⏱️  Time since last fetch: ${timeSinceFetch}ms (threshold: ${config.refreshThreshold}ms)`);
    
    // Prevent rapid refetches
    if (timeSinceFetch < config.refreshThreshold) {
      logger.debug(`[Cache] ⏭️  Skipping - too soon`);
      return;
    }
    
    // Try delta sync first if enabled
    const deltaEnabled = process.env.REACT_APP_DELTA_SYNC_ENABLED === 'true';
    logger.info(`[Cache] 🔍 Delta sync enabled: ${deltaEnabled}`);
    
    if (deltaEnabled) {
      const deltaResult = await this.tryDeltaSync(normalizedUrl);
      if (deltaResult) {
        logger.info(`[Cache] ✅ Background update via delta sync: ${normalizedUrl}`);
        return;
      } else {
        logger.warn(`[Cache] ⚠️  Delta sync failed or returned false, falling back to full fetch`);
      }
    }
    
    // Fallback to full fetch
    logger.info(`[Cache] 📥 Background update via full fetch: ${normalizedUrl}`);
    await this.fetchFresh(normalizedUrl, true);
  }

  /**
   * Try delta sync for URL
   * 
   * @param {string} url - API endpoint
   * @returns {Promise<boolean>} True if delta sync succeeded
   */
  async tryDeltaSync(url) {
    const entityType = this.getEntityType(url);
    logger.info(`[DeltaSync] 🎯 Trying delta sync for URL: ${url}`);
    logger.info(`[DeltaSync] 📦 Entity type detected: ${entityType}`);
    
    if (!entityType) {
      logger.warn(`[DeltaSync] ❌ No entity type found for ${url}`);
      return false;
    }
    
    // Check if we have a previous sync
    const hasSynced = DeltaSyncManager.hasSynced(entityType);
    const lastSyncTime = DeltaSyncManager.getLastSyncTime(entityType);
    logger.info(`[DeltaSync] 📊 Has synced before: ${hasSynced}, Last sync: ${lastSyncTime || 'never'}`);
    
    if (!hasSynced) {
      logger.warn(`[DeltaSync] ⚠️  No previous sync timestamp for ${entityType} - skipping delta sync`);
      return false;
    }
    
    try {
      logger.info(`[DeltaSync] 🚀 Attempting delta sync for ${entityType}...`);
      
      const changes = await DeltaSyncManager.deltaSync(entityType);
      
      logger.info(`[DeltaSync] 📨 Delta sync response:`, changes);
      
      if (!changes || !changes.isDelta) {
        logger.warn(`[DeltaSync] ⚠️  Response is not a delta (isDelta: ${changes?.isDelta})`);
        return false;
      }
      
      const totalChanges = changes.created.length + changes.updated.length + changes.deleted.length;
      logger.info(`[DeltaSync] ✅ Received ${totalChanges} changes for ${entityType}`, {
        created: changes.created.length,
        updated: changes.updated.length,
        deleted: changes.deleted.length
      });
      
      // Apply delta changes to cache
      if (totalChanges > 0) {
        logger.info(`[DeltaSync] 🔧 Applying changes to cache...`);
        this.applyDeltaChanges(url, changes);
      } else {
        logger.info(`[DeltaSync] ℹ️  No changes to apply`);
      }
      
      // Update timestamp
      this.lastFetchTimes[url] = Date.now();
      
      return true;
    } catch (error) {
      logger.error(`[DeltaSync] ❌ Failed for ${entityType}:`, error);
      return false;
    }
  }

  /**
   * Apply delta changes to cached data
   * 
   * @param {string} url - Cache key
   * @param {Object} changes - Delta changes
   */
  applyDeltaChanges(url, changes) {
    if (!this.cache.has(url)) {
      logger.warn(`[DeltaSync] No cache entry for ${url}`);
      return;
    }
    
    const cached = this.cache.get(url);
    let data = cached.data;
    
    // Handle different data structures
    let items = null;
    if (data && Array.isArray(data.data)) {
      items = data.data;
    } else if (Array.isArray(data)) {
      items = data;
    }
    
    if (!items) {
      logger.warn(`[DeltaSync] Unexpected data structure for ${url}`);
      return;
    }
    
    // Apply deletions
    changes.deleted.forEach(id => {
      const index = items.findIndex(item => item.id === id);
      if (index !== -1) {
        items.splice(index, 1);
        logger.debug(`[DeltaSync] Deleted item ${id}`);
      }
    });
    
    // Apply updates
    changes.updated.forEach(updatedItem => {
      const index = items.findIndex(item => item.id === updatedItem.id);
      if (index !== -1) {
        items[index] = updatedItem;
        logger.debug(`[DeltaSync] Updated item ${updatedItem.id}`);
      }
    });
    
    // Apply creations
    changes.created.forEach(newItem => {
      if (!items.find(item => item.id === newItem.id)) {
        items.push(newItem);
        logger.debug(`[DeltaSync] Created item ${newItem.id}`);
      }
    });
    
    // Update cache
    cached.timestamp = Date.now();
    this.saveToStorage(url, cached.data, cached.timestamp);
    
    // Notify subscribers
    this.notifySubscribers(url, cached.data);
    
    logger.info(`[DeltaSync] Applied ${changes.created.length + changes.updated.length + changes.deleted.length} changes to ${url}`);
  }

  /**
   * Fetch fresh data from API
   * 
   * @param {string} url - API endpoint
   * @param {boolean} silent - Don't notify subscribers (for background updates)
   * @returns {Promise<any>} Fresh data
   */
  async fetchFresh(url, silent = false) {
    try {
      const normalizedUrl = this.normalizeUrl(url);
      
      // Transform to V2 if delta sync enabled
      const deltaEnabled = process.env.REACT_APP_DELTA_SYNC_ENABLED === 'true';
      let requestUrl = normalizedUrl;
      
      if (deltaEnabled) {
        requestUrl = this.transformToV2(normalizedUrl);
      }
      
      // Add category filter for categories endpoint
      if (requestUrl.includes('/categories')) {
        const separator = requestUrl.includes('?') ? '&' : '?';
        requestUrl = `${requestUrl}${separator}parents_only=true`;
      }
      
      logger.debug(`[API] GET ${requestUrl}`);
      
      const response = await api.get(requestUrl);
      const data = this.extractData(response);
      
      // Record sync timestamp if V2 endpoint returned it
      logger.info(`[API] 🔍 Response data structure:`, {
        hasSyncTimestamp: !!response.data?.sync_timestamp,
        syncTimestamp: response.data?.sync_timestamp,
        deltaEnabled,
        url: requestUrl
      });
      
      if (deltaEnabled && response.data?.sync_timestamp) {
        const entityType = this.getEntityType(normalizedUrl);
        if (entityType) {
          DeltaSyncManager.syncTimestamps[entityType] = response.data.sync_timestamp;
          await DeltaSyncManager.saveSyncTimestamps();
          logger.info(`[DeltaSync] ✅ Recorded sync timestamp for ${entityType}: ${response.data.sync_timestamp}`);
        } else {
          logger.warn(`[DeltaSync] ⚠️  Could not determine entity type for ${normalizedUrl}`);
        }
      } else if (deltaEnabled && !response.data?.sync_timestamp) {
        // Only warn for list endpoints (not single product details)
        const isSingleProduct = requestUrl.includes('/v2/products/') && !requestUrl.includes('by-type');
        if (!isSingleProduct) {
          logger.warn(`[DeltaSync] ⚠️  V2 endpoint did not return sync_timestamp! URL: ${requestUrl}`);
        }
      }
      
      // Update cache
      this.cache.set(normalizedUrl, {
        data,
        timestamp: Date.now()
      });
      
      // Persist to localStorage
      this.saveToStorage(normalizedUrl, data, Date.now());
      
      // Update last fetch time
      this.lastFetchTimes[normalizedUrl] = Date.now();
      
      // Notify subscribers unless silent
      if (!silent) {
        this.notifySubscribers(normalizedUrl, data);
      }
      
      return data;
    } catch (error) {
      logger.error(`[API] Error fetching ${url}:`, error);
      throw error;
    }
  }

  /**
   * Subscribe to cache updates for a URL
   * 
   * @param {string} url - API endpoint
   * @param {Function} callback - Called when cache updates
   * @returns {Function} Unsubscribe function
   */
  subscribe(url, callback) {
    const normalizedUrl = this.normalizeUrl(url);
    
    if (!this.subscribers.has(normalizedUrl)) {
      this.subscribers.set(normalizedUrl, new Set());
    }
    
    this.subscribers.get(normalizedUrl).add(callback);
    
    // Return unsubscribe function
    return () => {
      const subs = this.subscribers.get(normalizedUrl);
      if (subs) {
        subs.delete(callback);
      }
    };
  }

  /**
   * Notify all subscribers of cache update
   * 
   * @param {string} url - Cache key
   * @param {any} data - Updated data
   */
  notifySubscribers(url, data) {
    const subs = this.subscribers.get(url);
    if (subs && subs.size > 0) {
      logger.debug(`[Cache] Notifying ${subs.size} subscribers for ${url}`);
      subs.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          logger.error('[Cache] Subscriber callback error:', error);
        }
      });
    }
  }

  /**
   * Extract data from API response
   */
  extractData(response) {
    if (response.data?.data !== undefined) {
      return response.data;
    }
    if (response.data?.categories !== undefined) {
      return response.data.categories;
    }
    if (response.data?.banners !== undefined) {
      return response.data.banners;
    }
    return response.data;
  }

  /**
   * Transform URL to V2 if needed
   * Only transforms endpoints that have V2 equivalents
   */
  transformToV2(url) {
    // Products - transform all product endpoints
    if (url.startsWith('/products')) {
      return url.replace('/products', '/v2/products');
    }
    
    // Categories - ONLY transform the list endpoint, NOT /categories/{slug}/products
    // /categories → /v2/categories (list endpoint - has V2)
    // /categories/{slug}/products → DO NOT transform (no V2 endpoint)
    if (url.startsWith('/categories')) {
      // Check if it's a category products request (has /products in path)
      if (url.includes('/products')) {
        // Don't transform - this is /categories/{slug}/products which has no V2
        return url;
      }
      return url.replace('/categories', '/v2/categories');
    }
    
    // Banners - transform all banner endpoints
    if (url.startsWith('/banners')) {
      return url.replace('/banners', '/v2/banners');
    }
    
    return url;
  }

  /**
   * Get entity type from URL for delta sync
   */
  getEntityType(url) {
    if (url.includes('/products')) return 'product';
    if (url.includes('/categories')) return 'category';
    if (url.includes('/banners')) return 'banner';
    return null;
  }

  /**
   * Get content type from URL
   */
  getContentType(url) {
    if (url.includes('/products')) return 'products';
    if (url.includes('/categories')) return 'categories';
    if (url.includes('/banners')) return 'banners';
    return 'default';
  }

  /**
   * Normalize URL for consistent cache keys
   */
  normalizeUrl(url) {
    try {
      if (!url.includes('?')) return url;
      
      const [baseUrl, queryString] = url.split('?');
      const params = new URLSearchParams(queryString);
      
      const sortedParams = Array.from(params.entries())
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
      
      const normalizedQuery = sortedParams
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
      
      return `${baseUrl}?${normalizedQuery}`;
    } catch (error) {
      return url;
    }
  }

  /**
   * Load from localStorage
   */
  loadFromStorage(key) {
    try {
      const stored = localStorage.getItem(this.storagePrefix + key);
      if (!stored) return null;
      
      const { data, timestamp } = JSON.parse(stored);
      const config = this.config[this.getContentType(key)] || this.config.default;
      const age = Date.now() - timestamp;
      
      return {
        data,
        timestamp,
        isStale: age > config.maxAge
      };
    } catch (error) {
      logger.error('[Cache] localStorage load error:', error);
      return null;
    }
  }

  /**
   * Save to localStorage
   */
  saveToStorage(key, data, timestamp) {
    try {
      localStorage.setItem(this.storagePrefix + key, JSON.stringify({ data, timestamp }));
    } catch (error) {
      logger.error('[Cache] localStorage save error:', error);
    }
  }

  /**
   * Clear cache for specific URL
   */
  invalidate(url) {
    const normalizedUrl = this.normalizeUrl(url);
    this.cache.delete(normalizedUrl);
    localStorage.removeItem(this.storagePrefix + normalizedUrl);
    logger.info(`[Cache] Invalidated: ${normalizedUrl}`);
  }

  /**
   * Clear all cache
   */
  clearAll() {
    this.cache.clear();
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.storagePrefix)) {
        localStorage.removeItem(key);
      }
    });
    logger.info('[Cache] Cleared all cache');
  }
}

// Export singleton instance
export default new CacheManager();
